import {
  getLocation,
  LOCATION_CHANGE,
  LocationChangeAction,
  RouterLocation,
} from "connected-react-router";
import { buffers, Channel } from "redux-saga";
import {
  actionChannel,
  call,
  flush,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
} from "redux-saga/effects";

import AccessChangeNotice from "@app/enum/AccessChangeNotice";
import AccessChangeOption from "@app/enum/AccessChangeOption";
import { isInitialized } from "@app/store/app";
import { initializeApp, reinitializeApp } from "@app/store/app/actions";
import { login, logout } from "@app/store/auth/actions";
import { handleError } from "@app/store/errors/actions";
import reloadPage from "@app/util/reloadPage";

import {
  accessChanged,
  chooseAccessChangeOption,
  clearAccessChange,
  showAccessChangeNotice,
  showAccessChangePrompt,
} from "./actions";
import { isReloadPending } from "./selectors";

function* waitForInitialization() {
  const initialized: boolean = yield select(isInitialized);
  if (initialized) {
    return true;
  }
  const { success } = yield race({
    failure: take(initializeApp.failure),
    success: take(initializeApp.success),
  });
  return !!success;
}

// Any change can matter to the open page, e.g. a hidden field or a step it already passed, and
// swapping access under it can clear its input or break its flow, so the user picks what happens
function* askHowToApply(changes?: Channel<ReturnType<typeof accessChanged>>) {
  yield put(showAccessChangePrompt());
  const { payload: option }: ReturnType<typeof chooseAccessChangeOption> =
    yield take(chooseAccessChangeOption);
  // A change that came in while the prompt was open is covered by this answer: a reload or the
  // fetch below picks it up, and a kept page stays pending. Left queued, it would ask again
  if (changes) {
    yield flush(changes);
  }
  return option;
}

export function* onAccessChanged(
  isOwnChange: boolean,
  changes?: Channel<ReturnType<typeof accessChanged>>
) {
  try {
    const reloadPending: boolean = yield select(isReloadPending);
    if (reloadPending) {
      return;
    }

    const initialized: boolean = yield call(waitForInitialization);
    if (!initialized) {
      return;
    }

    // The user's own change, e.g. renaming the org, is what they asked for, so it applies as
    // Update this page would, without asking
    const option: AccessChangeOption = isOwnChange
      ? AccessChangeOption.UPDATE_PAGE
      : yield call(askHowToApply, changes);
    if (option === AccessChangeOption.RELOAD) {
      yield call(reloadPage);
      return;
    }
    if (option === AccessChangeOption.KEEP_PAGE) {
      yield put(showAccessChangeNotice(AccessChangeNotice.RELOAD_PENDING));
      return;
    }

    // loaded only now, so an update applies the access the user has when they choose it
    yield put(reinitializeApp.request());
    const { failure }: { failure?: ReturnType<typeof reinitializeApp.failure> } = yield race({
      failure: take(reinitializeApp.failure),
      success: take(reinitializeApp.success),
    });
    if (failure) {
      yield call(offerReload, failure.payload);
    }
  } catch (error) {
    yield call(offerReload, error);
  }
}

// the page may still show the old access, and a reload is what fixes it
function* offerReload(error: unknown) {
  yield put(handleError({ displayNotification: false, error }));
  yield put(showAccessChangeNotice(AccessChangeNotice.RELOAD_PENDING));
}

export function* onSignOut() {
  yield put(clearAccessChange());
}

// The first path segment, e.g. "company": opening a record or a sub-page stays in the section
const getAppSection = (pathname: string) => pathname.split("/")[1];

export function* watchNavigation() {
  const location: RouterLocation<unknown> = yield select(getLocation);
  let currentSection = getAppSection(location.pathname);
  while (true) {
    const { payload }: LocationChangeAction = yield take(LOCATION_CHANGE);
    const section = getAppSection(payload.location.pathname);
    const isSectionChange = section !== currentSection;
    currentSection = section;
    const reloadPending: boolean = yield select(isReloadPending);
    if (isSectionChange && reloadPending) {
      yield call(reloadPage);
    }
  }
}

export function* accessChangeSaga() {
  yield fork(watchNavigation);
  yield takeEvery([login, logout], onSignOut);
  // A sliding buffer of one: changes arriving during a refresh collapse into exactly one more
  const channel: Channel<ReturnType<typeof accessChanged>> = yield actionChannel(
    accessChanged,
    buffers.sliding(1)
  );
  while (true) {
    const { payload }: ReturnType<typeof accessChanged> = yield take(channel);
    // a refresh must not outlive the session it was fetched for, nor must a change queued in it
    const { signOut } = yield race({
      refresh: call(onAccessChanged, payload.isOwnChange, channel),
      signOut: take([login, logout]),
    });
    if (signOut) {
      yield flush(channel);
    }
  }
}
